import React, { createContext, useContext, useMemo, useState } from 'react';

type OutputMaterialContextValue = {
  searchQuery: string;
  showOnlyUnMatched: boolean;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  setShowOnlyUnMatched: React.Dispatch<React.SetStateAction<boolean>>;
};

type OutputMaterialFilter = {
  searchQuery: string;
  showOnlyUnMatched: boolean;
};

const OutputMaterialContext = createContext<
  OutputMaterialContextValue | undefined
>(undefined);

type OutputMaterialProviderProps = {
  children: React.ReactNode;
};

export const OutputMaterialProvider = ({
  children,
}: OutputMaterialProviderProps) => {
  const [state, setState] = useState<OutputMaterialFilter>({
    searchQuery: '',
    showOnlyUnMatched: false,
  });

  const setSearchQuery: React.Dispatch<
    React.SetStateAction<string>
  > = value => {
    setState(prev => ({
      ...prev,
      searchQuery:
        typeof value === 'function' ? value(prev.searchQuery) : value,
    }));
  };

  const setShowOnlyUnMatched: React.Dispatch<
    React.SetStateAction<boolean>
  > = value => {
    setState(prev => ({
      ...prev,
      showOnlyUnMatched:
        typeof value === 'function' ? value(prev.showOnlyUnMatched) : value,
    }));
  };

  const value = useMemo(
    () => ({
      searchQuery: state.searchQuery,
      showOnlyUnMatched: state.showOnlyUnMatched,
      setSearchQuery,
      setShowOnlyUnMatched,
    }),
    [state.searchQuery, state.showOnlyUnMatched]
  );

  return (
    <OutputMaterialContext.Provider value={value}>
      {children}
    </OutputMaterialContext.Provider>
  );
};

export const useOutputMaterialContext = () => {
  const context = useContext(OutputMaterialContext);

  if (!context) {
    throw new Error(
      'useOutputMaterialContext must be used within an OutputMaterialProvider'
    );
  }

  return context;
};
